/**
 * ProgressSteps — Step indicator with dots and connecting lines
 *
 * Used for: multi-step forms (create store wizard)
 * From: CreateStoreScreen stepIndicator pattern
 */
import type { ReactNode } from 'react'
import { styled, XStack, YStack, type GetProps } from 'tamagui'

const StepDot = styled(XStack, {
  name: 'StepDot',
  w: 24,
  h: 24,
  br: '$full',
  ai: 'center',
  jc: 'center',
  bg: '$backgroundSecondary',

  variants: {
    state: {
      pending: { bg: '$backgroundSecondary' },
      active: { bg: '$colorPrimary' },
      completed: { bg: '$colorPrimary' },
    },
  } as const,

  defaultVariants: {
    state: 'pending',
  },
})

const StepLine = styled(YStack, {
  name: 'StepLine',
  h: 2,
  f: 1,
  mx: 4,
  bg: '$backgroundSecondary',

  variants: {
    completed: {
      true: { bg: '$colorPrimary' },
    },
  } as const,
})

type ProgressStepsFrameProps = GetProps<typeof XStack>

interface ProgressStepsProps extends ProgressStepsFrameProps {
  /** Total number of steps */
  steps: number
  /** Current active step (0-indexed) */
  currentStep: number
  /** Optional icon rendered inside completed dots */
  completedIcon?: ReactNode
}

export const ProgressSteps = XStack.styleable<ProgressStepsProps>(
  ({ steps, currentStep, completedIcon, ...props }, ref) => {
    return (
      <XStack ref={ref} ai="center" jc="center" {...props}>
        {Array.from({ length: steps }).map((_, i) => {
          const state = i < currentStep ? 'completed' : i === currentStep ? 'active' : 'pending'
          return (
            <XStack key={i} f={1} ai="center">
              <StepDot state={state}>
                {state === 'completed' && completedIcon}
              </StepDot>
              {i < steps - 1 && (
                <StepLine completed={i < currentStep} />
              )}
            </XStack>
          )
        })}
      </XStack>
    )
  },
)
